import TR from 'utils/TR';
import useRect from 'hooks/common/useRect';
import React from 'react';
import T from 'utils/T';
import ex from 'utils/ex';

import * as Luxon from 'luxon';
import VBox from 'components/common/VBox';
import Search from 'components/common/Search';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';

import './FilterSelect.css';
import WheelScroll from 'components/common/WheelScroll';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import focusElement from 'utils/focusElement';
import hotkey from 'utils/hotkey';
import useLayoutRect from 'hooks/common/useLayoutRect';
import useForceUpdate from 'hooks/common/useForceUpdate';
import { repeatAsync } from 'utils/asynchrous';

export default function FilterSelect(props) {
  const {
    id,
    reference, // Element
    placement,
    connection, // 'start-arrow' (default) | 'line' | 'end-arrow' | 'arrow' | 'none'
    width, // integer | 'auto'
    height, // integer | 'auto'
    veilable, // Boolean
    stickable, // Boolean
    movable, // Boolean
    closable, // Boolean
    escapable, // Boolean
    modal, // Boolean
    dim, // color (default=transparent)
    tags = [],
    type,
    title,
    description,
    contents,
    buttons = [],
    events = {},
    props: {
      size: ROW_LENGTH = 7,
      item_list,
      defaultValue,
      renderer,
    },
    close,
    ...rest
  } = props || {};

  const today = React.useMemo(() => Luxon.DateTime.now(), []);

  const ref = React.useRef();
  const searchRef = React.useRef();
  const [value, setValue] = React.useState('');
  const rect = useLayoutRect(reference);
  const floatingRect = useRect(ref);

  React.useEffect(() => {
    const cancel = hotkey(['Escape']);
    const onKeyDown = (e) => {
      cancel(e, () => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target === searchRef.current) {
          setValue(() => '');
        } else {
          return events?.onClose();
        }
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events?.onClose]);

  const style = React.useMemo(() => {
    const info = {};
    switch (placement) {
      case 'top-left': {
        break;
      }
      case 'top': {
        break;
      }
      case 'top-right': {
        break;
      }
      case 'bottom-left': {
        break;
      }
      case 'bottom': {
        break;
      }
      case 'bottom-right': {
        const top = rect.top + rect.height;
        const right = window.innerWidth - (rect.left + rect.width);
        info.top = top;
        info.right = right;
        break;
      }
      case 'left-top': {
        break;
      }
      case 'left': {
        break;
      }
      case 'left-bottom': {
        break;
      }
      case 'right-top': {
        break;
      }
      case 'right': {
        break;
      }
      case 'right-bottom': {
        break;
      }
      default: {
        break;
      }
    }
    return info;
  }, [placement, rect.height, rect.left, rect.top, rect.width, floatingRect.top, floatingRect.left]);


  const onChange = React.useCallback((e) => {
    setRowIndex(() => 0);
    setValue(() => e.target.value);
  }, []);

  const [rowIndex, setRowIndex] = React.useState(0);
  const rowIndexRef = React.useRef(rowIndex);
  React.useEffect(() => {
    rowIndexRef.current = rowIndex;
  }, [rowIndex]);

  const filtered_list = React.useMemo(() => item_list.filter(item => 0 <= item?.name.indexOf(value.trim())).map((p, index) => ({
    ...p,
    index,
  })), [item_list, value]);
  const max_list_length = React.useMemo(() => Math.min(ROW_LENGTH, filtered_list.length), [ROW_LENGTH, filtered_list.length]);

  const forceUpdate = useForceUpdate();
  React.useEffect(() => {
    const defaultValueIndex = Math.max(0, filtered_list.findIndex(item => item.key === defaultValue));
    setRowIndex(() => Math.max(0, Math.min(filtered_list.length - max_list_length, defaultValueIndex - 3)));
    repeatAsync(() => {
      if (value.length) return true;
      const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')];
      const tabElementIndex = tabElements.findIndex(e => +e.getAttribute('data-index') === +defaultValueIndex);
      return 0 <= tabElementIndex;
    }, () => {
      if (value.length) return true;
      const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')];
      const tabElementIndex = tabElements.findIndex(e => +e.getAttribute('data-index') === +defaultValueIndex);
      tabElements[tabElementIndex]?.focus();
      // 포커스가 변하면서 기존 reference의 위치가 변할 경우를 위해 한번 더 렌더링
      requestAnimationFrame(() => {
        forceUpdate();
      });
    }, 100);
  }, [forceUpdate, filtered_list, value, max_list_length, defaultValue]);

  const sliced_list = React.useMemo(() => filtered_list.slice(rowIndex, rowIndex + max_list_length), [filtered_list, rowIndex, max_list_length]);

  React.useEffect(() => {
    const forwardA = hotkey(['ArrowDown']);
    const backwardA = hotkey(['ArrowUp']);
    const forwardT = hotkey(['Tab']);
    const backwardT = hotkey(['Shift+Tab']);
    const onKeyDown = (e) => {
      if (e.key === 'Enter') return;
      const onForward = (e) => {
        e.preventDefault();
        e.stopPropagation();
        focusElement(document.activeElement, 1, (e) => {
          if (document.activeElement.getAttribute('data-select-id') !== id) return true;
          if (e.getAttribute('data-select-id') !== id) {
            setRowIndex(r => 0);
            requestAnimationFrame(() => {
              const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')];
              tabElements[0]?.focus();
            });
            return false;
          }
          const dataIndex = +e.getAttribute('data-index');
          const rowIndex = +e.getAttribute('data-row-index');
          const halfRow = ROW_LENGTH * 0.5;
          if (rowIndex < halfRow) return true;
          if (filtered_list.length - halfRow < dataIndex) return true;
          setRowIndex(r => Math.max(0, Math.min(filtered_list.length - ROW_LENGTH, r + 1)));
          return true;
        });
      };
      const onBackward = (e) => {
        e.preventDefault();
        e.stopPropagation();
        focusElement(document.activeElement, -1, (e) => {
          if (document.activeElement.getAttribute('data-select-id') !== id) return true;
          if (e.getAttribute('data-select-id') !== id) {
            setRowIndex(r => filtered_list.length - ROW_LENGTH);
            requestAnimationFrame(() => {
              const tabElements = [...document.querySelectorAll('[data-select-id][tabIndex]')].reverse();
              tabElements[0]?.focus();
            });
            return false;
          }
          const dataIndex = +e.getAttribute('data-index');
          const rowIndex = +e.getAttribute('data-row-index');
          const halfRow = ROW_LENGTH * 0.5;
          if (halfRow - 1 < rowIndex) return true;
          if (dataIndex < halfRow - 1) return true;
          setRowIndex(r => Math.max(0, Math.min(filtered_list.length - ROW_LENGTH, r - 1)));
          return true;
        });
      }
      forwardA(e, onForward);
      forwardT(e, onForward);
      backwardA(e, onBackward);
      backwardT(e, onBackward);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [ROW_LENGTH, id, max_list_length, filtered_list.length]);

  const onWheelUpward = React.useCallback((e) => {
    setRowIndex(rowIndex => {
      return Math.min(filtered_list.length - max_list_length, Math.max(0, rowIndex + 1));
    })
  }, [filtered_list.length, max_list_length]);
  const onWheelDownward = React.useCallback((e) => {
    setRowIndex(rowIndex => {
      return Math.min(filtered_list.length - max_list_length, Math.max(0, rowIndex - 1));
    });
  }, [filtered_list.length, max_list_length]);

  const onSearchKeyDown = React.useCallback((e) => {
    switch (e.key) {
      case 'Enter':
        break;
      default:
        break;
    }
  }, []);

  const onButtonKeyDown = React.useCallback((e) => {
    switch (e.key) {
      case 'Shift':
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Tab':
        return;
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        setRowIndex(() => 0);
        setValue(() => e.target.getAttribute('data-name'));
        break;
      default:
        console.log(e.key);
        requestAnimationFrame(() => {
          searchRef.current?.focus();
        });
        break;
    }
  }, []);
  return (
    <div
      {...ex('Dim')}
      {...{ onClick: events?.onOuterClick(ref) }}
    >
      <VBox
        {...ex('FilterSelect', rest)}
        {...{
          ref,
          style,
        }}
      >
        <Search
          {...{
            ref: searchRef,
            placeholder: T('환자검색'),
            value,
            onChange,
            onKeyDown: onSearchKeyDown,
          }}
        />
        <WheelScroll
          {...ex('Contents')}
          {...{
            onWheelUpward,
            onWheelDownward,
            total: filtered_list.length,
            size: max_list_length,
            current: rowIndex,
          }}
        >
          {sliced_list.map((item, index) => {
            return (
              <FocusableButton
                key={item?.key}
                {...ex('PatientItem')}
                {...{
                  onClick: events?.onSubmit(item?.key),
                  onKeyDown: onButtonKeyDown,
                  'data-select-id': id,
                  'data-index': item?.index,
                  'data-row-index': index,
                }}
              >
                {renderer(item)}
              </FocusableButton>
            )
          })}
        </WheelScroll>
      </VBox>
    </div>
  );
}
